// RC-010/44.1 — formatação de INSTANTE no fuso do TENANT (lado web, via Intl).
// Espelho dos helpers do server (apps/api/src/common/utils/tenant-local-time.ts):
// date-fns format()/toLocale*String sem timeZone renderizam no fuso do DEVICE — quem
// abre de outro fuso vê hora diferente da recepção. Superfícies de data/hora usam
// ESTES helpers com o tz de useTenantTimezone() (gate: tz-format-check-web.py).

import { parseLocalDate } from '@/lib/dates';

type InstantInput = string | Date | null | undefined;

/** Data civil YYYY-MM-DD da clínica, inclusive após a virada do dia em UTC. */
export function tenantCalendarDate(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function toValidDate(input: InstantInput): Date | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "14:00" no fuso do tenant. */
export function tenantClockLabel(input: InstantInput, timeZone: string): string {
  const d = toValidDate(input);
  if (!d) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone, hour: '2-digit', minute: '2-digit' }).format(d);
}

/** "05/08" no fuso do tenant. */
export function tenantShortDateLabel(input: InstantInput, timeZone: string): string {
  const d = toValidDate(input);
  if (!d) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: '2-digit' }).format(d);
}

/** "05/08/2026" no fuso do tenant (histórico plurianual — anotações da ficha, 45.2/45.6). */
export function tenantDateLabel(input: InstantInput, timeZone: string): string {
  const d = toValidDate(input);
  if (!d) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/** "05/08 às 14:00" (separador configurável — DeliveriesTable usa espaço). */
export function tenantShortDateTimeLabel(input: InstantInput, timeZone: string, separator = ' às '): string {
  const date = tenantShortDateLabel(input, timeZone);
  const clock = tenantClockLabel(input, timeZone);
  return date && clock ? `${date}${separator}${clock}` : '';
}

/** "quarta-feira, 5 de agosto às 14:00" no fuso do tenant (título do detalhe). */
export function tenantLongDateTimeLabel(input: InstantInput, timeZone: string): string {
  const d = toValidDate(input);
  if (!d) return '';
  const datePart = new Intl.DateTimeFormat('pt-BR', { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  return `${datePart} às ${tenantClockLabel(d, timeZone)}`;
}

/** Dia civil YYYY-MM-DD → número de dia UTC (aritmética de dias sem fuso). */
function calendarKeyToUtcDay(key: string): number | null {
  const d = parseLocalDate(key);
  if (!d) return null;
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Dias-calendário no fuso do tenant entre dois instantes (0 = mesmo dia local). Story 59.2. */
export function tenantCalendarDaysBetween(
  timeZone: string,
  from: InstantInput,
  to: InstantInput = new Date(),
): number {
  const a = toValidDate(from);
  const b = toValidDate(to);
  if (!a || !b) return 0;
  const ua = calendarKeyToUtcDay(tenantCalendarDate(timeZone, a));
  const ub = calendarKeyToUtcDay(tenantCalendarDate(timeZone, b));
  if (ua == null || ub == null) return 0;
  return Math.round((ub - ua) / 86_400_000);
}

/** Data civil YYYY-MM-DD da clínica N dias à frente (defaults de prazo). Story 59.2. */
export function tenantCalendarDatePlusDays(timeZone: string, days: number, now = new Date()): string {
  const d = parseLocalDate(tenantCalendarDate(timeZone, now));
  if (!d) return '';
  const shifted = new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`;
}
